import React from "react";

/**
 * Narrow shape for SentiLink rows passed into ApplicantSummaryV2 as legacyApp.sentilink.
 * Kept local to `shared` so this component does not import manager mapper types.
 */
export interface SentilinkIdentityPayload {
  triggered: boolean;
  outcome: string | null;
  riskTier: string | null;
  reasonCodes: ReadonlyArray<{ description?: string }>;
  syntheticReasonCodes?: ReadonlyArray<{ description?: string }>;
  identityManuallyVerified?: boolean;
  identityManualVerification?: {
    reviewerName?: string | null;
    reviewerEmail?: string | null;
    reviewedItem?: string | null;
    reviewedItemLabel?: string | null;
    details?: string | null;
    verifiedAt?: string | null;
  } | null;
}

/** Deal-summary reason lists are labelled by check (PRODUCT-2551). */
export const SENTILINK_ID_THEFT_CHECK_LABEL = "ID Theft";
export const SENTILINK_SYNTHETIC_CHECK_LABEL = "Synthetic";

/** PRODUCT-2699: canonical manager-facing label for the SentiLink identity check. */
export const SENTILINK_IDENTITY_DISPLAY_NAME = "Identity Risk Check";

/** PRODUCT-2724: empty Needs Review / High Risk check after more_fraudy filter. */
export const SENTILINK_NO_RISK_RAISING_SIGNALS_COPY = "No risk-raising signals";

const RISK_TIER_LABEL: Record<string, string> = {
  LOW_RISK: "Low Risk",
  NEEDS_REVIEW: "Needs Review",
  HIGH_RISK: "High Risk",
};

/** Modal reason lines are shown only for these tiers (PRODUCT-1283). */
const RISK_TIERS_WITH_MODAL_REASONS = new Set(["NEEDS_REVIEW", "HIGH_RISK"]);
const SENTILINK_TASK_PREFIX = "sentilink:";

function taskListIncludesSentilink(value: unknown): boolean {
  return (
    Array.isArray(value) &&
    value.some(
      (task) =>
        typeof task === "string" &&
        task.toLowerCase().includes(SENTILINK_TASK_PREFIX)
    )
  );
}

function taskGroupsIncludeSentilink(value: unknown): boolean {
  if (!Array.isArray(value)) {
    return false;
  }

  return value.some((group) => {
    if (!group || typeof group !== "object") {
      return false;
    }

    const tasks = group as Record<string, unknown>;
    return (
      ["complete", "next", "possible"].some((bucket) =>
        taskListIncludesSentilink(tasks[bucket])
      ) || taskGroupsIncludeSentilink(tasks.groups)
    );
  });
}

/**
 * The workflow run is the historical source of truth for whether SentiLink
 * applied to an application. Inspect only persisted tasks and formula history,
 * rather than arbitrary response or explanation strings on the run.
 */
export function workflowIncludesSentilink(workflow: unknown): boolean {
  if (!workflow || typeof workflow !== "object") {
    return false;
  }

  const run = workflow as Record<string, unknown>;
  const tasks = run.tasks as Record<string, unknown> | undefined;
  if (
    ["complete", "next", "possible"].some((bucket) =>
      taskListIncludesSentilink(tasks?.[bucket])
    ) ||
    taskGroupsIncludeSentilink(tasks?.groups)
  ) {
    return true;
  }

  return (
    Array.isArray(run.history) &&
    run.history.some((entry) => {
      const formula =
        entry && typeof entry === "object"
          ? (entry as Record<string, unknown>).formula
          : undefined;
      return typeof formula === "string" && formula.toLowerCase().includes("sentilink");
    })
  );
}

/**
 * A completed, skipped, reset, or manually verified check is durable evidence
 * that SentiLink applied, even while the historical workflow request loads or
 * cannot be retrieved.
 */
export function shouldShowSentilink(
  sentilink: unknown,
  workflow: unknown,
  workflowLoading = false,
  workflowLookupFailed = false
): boolean {
  if (sentilink && typeof sentilink === "object") {
    const value = sentilink as Record<string, unknown>;
    if (
      value.triggered === true ||
      value.identityManuallyVerified === true ||
      value.notTriggeredStatus === "SKIPPED_NO_SSN" ||
      (typeof value.resetCount === "number" && value.resetCount > 0)
    ) {
      return true;
    }
  }

  // Do not decide from live company-level SentiLink data before the historical
  // workflow lookup settles. On a lookup failure, preserve the SentiLink block
  // rather than silently hiding a potentially pending fraud check.
  if (workflowLoading) {
    return false;
  }
  if (workflowLookupFailed) {
    return true;
  }

  return workflowIncludesSentilink(workflow);
}

function formatSentilinkInlineValue(detail: string): string {
  return `${SENTILINK_IDENTITY_DISPLAY_NAME}: ${detail}`;
}

function parseSentilinkInlineDetail(value: string): string | null {
  const prefix = `${SENTILINK_IDENTITY_DISPLAY_NAME}:`;
  if (!value.startsWith(prefix)) {
    return null;
  }
  const detail = value.slice(prefix.length).trim();
  return detail || null;
}

export type SentilinkDisplayRowKind = "error" | "tier";

export interface SentilinkDisplayRow {
  kind: SentilinkDisplayRowKind;
  value: string;
}

/** Plain label/value rows for ApplicantGroupCard (deals list V2) and similar. */
export type SentilinkCardDetail = { label?: string; value: string };

/** Modal row shape compatible with ApplicantDetailModal (manager). */
export interface SentilinkModalDetailItem {
  label: string;
  value: string;
  subItems?: Array<{
    label: string;
    value: string;
    id?: string;
  }>;
  id?: string;
}

export function getSentilinkEmptyCheckModalSubItems(
  tierKey?: string | null
): NonNullable<SentilinkModalDetailItem["subItems"]> | undefined {
  const key = tierKey ? String(tierKey).toUpperCase() : "";
  if (!RISK_TIERS_WITH_MODAL_REASONS.has(key)) {
    return undefined;
  }
  return [
    {
      label: SENTILINK_ID_THEFT_CHECK_LABEL,
      value: SENTILINK_NO_RISK_RAISING_SIGNALS_COPY,
      id: "sentilink-modal-reason-0",
    },
    {
      label: SENTILINK_SYNTHETIC_CHECK_LABEL,
      value: SENTILINK_NO_RISK_RAISING_SIGNALS_COPY,
      id: "sentilink-modal-reason-1",
    },
  ];
}

/**
 * Accepts mapper-shaped `App.sentilink` plus tolerant partial payloads (e.g. missing
 * `reasonCodes` key → treated as empty list).
 */
function normalizeSentilinkInput(
  value: unknown
): SentilinkIdentityPayload | null {
  if (!value || typeof value !== "object") {
    return null;
  }
  const o = value as Record<string, unknown>;
  if (typeof o.triggered !== "boolean") {
    return null;
  }
  if (o.reasonCodes != null && !Array.isArray(o.reasonCodes)) {
    return null;
  }
  if (o.syntheticReasonCodes != null && !Array.isArray(o.syntheticReasonCodes)) {
    return null;
  }
  const reasonCodes = Array.isArray(o.reasonCodes) ? o.reasonCodes : [];
  const syntheticReasonCodes = Array.isArray(o.syntheticReasonCodes)
    ? o.syntheticReasonCodes
    : [];
  const outcomeRaw = o.outcome;
  const riskRaw = o.riskTier;
  return {
    triggered: o.triggered,
    outcome:
      outcomeRaw == null || outcomeRaw === ""
        ? null
        : String(outcomeRaw as string | number),
    riskTier:
      riskRaw == null || riskRaw === ""
        ? null
        : String(riskRaw as string | number),
    reasonCodes,
    syntheticReasonCodes,
    identityManuallyVerified: o.identityManuallyVerified === true,
    identityManualVerification:
      o.identityManualVerification &&
      typeof o.identityManualVerification === "object"
        ? (o.identityManualVerification as SentilinkIdentityPayload["identityManualVerification"])
        : null,
  };
}

function descriptionsFromCodes(
  codes: ReadonlyArray<{ description?: string }> | undefined
): string[] {
  if (!codes?.length) {
    return [];
  }
  return codes
    .map((rc) =>
      typeof rc?.description === "string" ? rc.description.trim() : ""
    )
    .filter(Boolean);
}

function collectReasonDescriptions(
  payload: SentilinkIdentityPayload
): string[] {
  const lines: string[] = [];
  for (const description of descriptionsFromCodes(payload.reasonCodes)) {
    lines.push(`${SENTILINK_ID_THEFT_CHECK_LABEL}: ${description}`);
  }
  for (const description of descriptionsFromCodes(
    payload.syntheticReasonCodes
  )) {
    lines.push(`${SENTILINK_SYNTHETIC_CHECK_LABEL}: ${description}`);
  }
  return lines;
}

/**
 * Tier or error line only — shown on the identity card / ServiceCard (no reason lines).
 */
export function buildSentilinkInlineRows(
  sentilink: unknown
): SentilinkDisplayRow[] {
  const payload = normalizeSentilinkInput(sentilink);
  if (!payload || !payload.triggered) {
    return [];
  }

  if (payload.identityManuallyVerified) {
    return [
      { kind: "tier", value: formatSentilinkInlineValue("Manually verified") },
    ];
  }

  const outcome = payload.outcome
    ? String(payload.outcome).toUpperCase()
    : null;
  if (outcome === "ERROR") {
    return [{ kind: "error", value: formatSentilinkInlineValue("Error") }];
  }

  const tierKey =
    payload.riskTier != null && payload.riskTier !== ""
      ? String(payload.riskTier).toUpperCase()
      : null;
  const tierLabel = tierKey ? RISK_TIER_LABEL[tierKey] : null;
  if (!tierLabel) {
    return [];
  }

  return [{ kind: "tier", value: formatSentilinkInlineValue(tierLabel) }];
}

/**
 * Reason descriptions for the ID Verification modal (Needs Review and High Risk only).
 * Both id-theft and synthetic lists are included, labelled by check (PRODUCT-2551).
 */
export function buildSentilinkReasonDescriptions(
  sentilink: unknown
): string[] {
  const payload = normalizeSentilinkInput(sentilink);
  if (!payload || !payload.triggered) {
    return [];
  }

  const outcome = payload.outcome
    ? String(payload.outcome).toUpperCase()
    : null;
  if (outcome === "ERROR") {
    return [];
  }

  const tierKey =
    payload.riskTier != null && payload.riskTier !== ""
      ? String(payload.riskTier).toUpperCase()
      : null;
  if (!tierKey || !RISK_TIERS_WITH_MODAL_REASONS.has(tierKey)) {
    return [];
  }

  return collectReasonDescriptions(payload);
}

export function getSentilinkCardDetails(
  sentilink: unknown
): SentilinkCardDetail[] {
  return buildSentilinkInlineRows(sentilink).map((r) => ({ value: r.value }));
}

export type SentilinkRiskTierKey = "LOW_RISK" | "NEEDS_REVIEW" | "HIGH_RISK";
export type SentilinkNotTriggeredKind = "AWAITING" | "SKIPPED" | "NOT_STARTED";

/**
 * Normalized SentiLink view-model shared by the deals-list card and the ID
 * Verification detail modal, so the two surfaces cannot disagree about what
 * counts as blocked / error / a tier / a not-triggered sub-state (PRODUCT-1346).
 */
export interface SentilinkView {
  triggered: boolean;
  isError: boolean;
  isBlocked: boolean;
  isManuallyVerified: boolean;
  tierKey: SentilinkRiskTierKey | null;
  tierLabel: string | null;
  notTriggered: SentilinkNotTriggeredKind | null;
  reasons: string[];
  resetCount: number;
  resetLimit: number | null;
  manualVerification: SentilinkIdentityPayload["identityManualVerification"];
}

export function deriveSentilinkView(sentilink: unknown): SentilinkView | null {
  const payload = normalizeSentilinkInput(sentilink);
  if (!payload) {
    return null;
  }
  const raw = sentilink as Record<string, unknown>;

  const outcome = payload.outcome ? payload.outcome.toUpperCase() : null;
  const tierRaw = payload.riskTier ? payload.riskTier.toUpperCase() : null;
  const tierKey =
    tierRaw && Object.prototype.hasOwnProperty.call(RISK_TIER_LABEL, tierRaw)
      ? (tierRaw as SentilinkRiskTierKey)
      : null;

  const { triggered } = payload;
  // Only a "BLOCK" gate outcome blocks; a HIGH_RISK tier can still pass the gate.
  const isError = triggered && outcome === "ERROR";
  const isBlocked = triggered && outcome === "BLOCK";
  const isManuallyVerified = payload.identityManuallyVerified === true;

  const resetCount = typeof raw.resetCount === "number" ? raw.resetCount : 0;
  const resetLimit = typeof raw.resetLimit === "number" ? raw.resetLimit : null;

  let notTriggered: SentilinkNotTriggeredKind | null = null;
  if (!triggered) {
    if (resetCount > 0) {
      notTriggered = "AWAITING";
    } else if (raw.notTriggeredStatus === "SKIPPED_NO_SSN") {
      notTriggered = "SKIPPED";
    } else {
      notTriggered = "NOT_STARTED";
    }
  }

  return {
    triggered,
    isError,
    isBlocked,
    isManuallyVerified,
    tierKey,
    tierLabel: tierKey ? RISK_TIER_LABEL[tierKey] : null,
    notTriggered,
    reasons: buildSentilinkReasonDescriptions(sentilink),
    resetCount,
    resetLimit,
    manualVerification: payload.identityManualVerification ?? null,
  };
}

function getSentilinkModalLabelValue(
  primary: SentilinkDisplayRow
): { label: string; value: string } {
  if (primary.kind === "error") {
    return { label: SENTILINK_IDENTITY_DISPLAY_NAME, value: "Error" };
  }
  const detail = parseSentilinkInlineDetail(primary.value);
  return {
    label: SENTILINK_IDENTITY_DISPLAY_NAME,
    value: detail ?? primary.value,
  };
}

/**
 * SentiLink section for the ID Verification detail modal (tier + reason lines).
 */
export function getSentilinkModalDetailItems(
  sentilink: unknown
): SentilinkModalDetailItem[] {
  const inline = buildSentilinkInlineRows(sentilink);
  if (inline.length === 0) {
    return [];
  }

  const reasons = buildSentilinkReasonDescriptions(sentilink);
  const primary = inline[0];
  const { label, value } = getSentilinkModalLabelValue(primary);

  if (reasons.length === 0) {
    const payload = normalizeSentilinkInput(sentilink);
    const emptySubItems = getSentilinkEmptyCheckModalSubItems(
      payload?.riskTier
    );
    return [
      {
        label,
        value,
        id: "sentilink-modal-tier",
        ...(emptySubItems ? { subItems: emptySubItems } : {}),
      },
    ];
  }

  return [
    {
      label,
      value,
      id: "sentilink-modal-tier",
      subItems: reasons.map((desc, idx) => {
        const sep = desc.indexOf(": ");
        return {
          label: sep > 0 ? desc.slice(0, sep) : "",
          value: sep > 0 ? desc.slice(sep + 2) : desc,
          id: `sentilink-modal-reason-${idx}`,
        };
      }),
    },
  ];
}

/**
 * PRODUCT-1283: tier/error line inside the identity ServiceCard (below Persona / document).
 * Reason descriptions are shown in the ID Verification modal, not inline.
 */
export function renderSentilinkIdentityLines(
  sentilink: unknown
): React.ReactNode {
  const rows = buildSentilinkInlineRows(sentilink);
  if (rows.length === 0) {
    return null;
  }

  return (
    <>
      {rows.map((row, idx) => {
        const testId =
          row.kind === "error"
            ? "sentilink-identity-error"
            : "sentilink-identity-tier";

        return (
          <span
            key={`sentilink-${row.kind}-${idx}`}
            className="vega label smaller"
            data-testid={testId}
          >
            {row.value}
          </span>
        );
      })}
    </>
  );
}
